import * as THREE from 'three';
import { cloth, emis, leather, metal, smoke, std } from '../materials.ts';
import { G, M, Ribbon, V, eyesGlow, limb, ring } from '../prims.ts';
import { figure } from './heroes.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * The Long Hunt (docs/sendas/barbaro.md, chapter 1): its people and its wolves, painted miniatures.
 *
 * - Bera, la Tuerta: eighty-three, small and wiry, a leather patch over the left eye, white hair cropped short with one
 *   thin braid, a fur collar over dark wool, her hunting stripe of red mud across the good eye, an oar over her shoulder
 *   (she rows), a horn at her belt.
 * - Ivar: Ulla's cousin, the clan's best hunter, a head taller than the hero; a braided beard in two plaits, the long
 *   hair tied back, a bearskin over the shoulders, a red sash, his grandfather Hrolf's spear with its old iron head.
 * - Terrón: Bera's stone boar (drawn by engine/caza.ts with the herd's legs, so it walks; his figure here is only where
 *   the board puts his name).
 * - The howler: a big ash wolf, darker than the pack, a mane standing up over its shoulders that smokes away at the tips,
 *   red eyes, a scar across the muzzle.
 * - The Grey Wolf: an ash wolf the size of a horse, pale grey, its coat coming apart into smoke at the ends, red eyes.
 *
 * Colors: cold charcoal and slate, pale skin, the clan's red (#a43a2c), the dragon's red for eyes; no gold.
 */

const CLAN = '#a43a2c';

/** An ash wolf (the pack's own rig and bones, so the wolves' clips move it): its smoke, its eyes, its mane. */
const ashWolf = (o: BaseOpts | undefined, look: { fur: string; alpha: number; eyes: string; mane?: string; seed: number }): Model => {
  const c = begin(o, 0.7, look.seed);
  const { g, R, rng } = c;
  const mat = smoke(look.fur, look.alpha, { depthWrite: true });
  const ribs: Ribbon[] = [];
  const body = joint(g, V(0, 0.55, -0.05), 'body');
  const chest = joint(body.abs, V(0, 0.6, 0.2), 'chest');
  const neck = joint(chest.abs, V(0, 0.7, 0.45), 'head');
  const jaw = joint(neck.abs, V(0, 0.735, 0.645), 'jaw');
  const bodyM = M(new THREE.SphereGeometry(0.26, 12, 9), mat, body.abs);
  bodyM.scale.set(1, 0.85, 2);
  bodyM.position.y = 0.55;
  M(new THREE.SphereGeometry(0.25, 12, 9), mat, chest.abs).position.set(0, 0.62, 0.32);
  const head = G(neck.abs, 0, 0.78, 0.58);
  M(new THREE.SphereGeometry(0.15, 10, 8), mat, head);
  const snout = M(new THREE.ConeGeometry(0.08, 0.26, 7), mat, head);
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, -0.03, 0.19);
  const jawG = G(jaw.abs, 0, 0.78, 0.58);
  const lower = M(new THREE.ConeGeometry(0.05, 0.18, 6), mat, jawG);
  lower.rotation.x = Math.PI / 2;
  lower.position.set(0, -0.045, 0.155);
  for (const k of [-1, 1]) M(new THREE.ConeGeometry(0.05, 0.15, 5), mat, head).position.set(k * 0.08, 0.15, -0.02);
  eyesGlow(head, look.eyes, 0.03, 0.12, 0.065);
  const skel: Record<string, THREE.Object3D> = { body: body.p, chest: chest.p, head: neck.p, jaw: jaw.p };
  for (const [x, z] of [[0.13, 0.35], [-0.13, 0.35], [0.13, -0.3], [-0.13, -0.3]] as const) {
    const front = z > 0;
    const side = x < 0 ? 'L' : 'R';
    const name = front ? 'fore' : 'hind';
    const leg = joint(front ? chest.abs : body.abs, V(x, 0.5, z), `${name}${side}`);
    const low = joint(leg.abs, V(x, 0.25, z + 0.05), `${name}${side}2`);
    limb(leg.abs, V(x, 0.5, z), V(x, 0.25, z + 0.05), 0.055, 0.065, mat, 6);
    limb(low.abs, V(x, 0.25, z + 0.05), V(x, 0.02, z), 0.04, 0.05, mat, 6);
    skel[`${name}${side}`] = leg.p;
    skel[`${name}${side}2`] = low.p;
  }
  const tail: THREE.Vector3[] = [];
  for (let i = 0; i < 7; i++) tail.push(V(0, 0.6 + i * 0.03, -0.45 - i * 0.07));
  let parent = body.abs;
  for (let s = 0; s < 3; s++) {
    const tj = joint(parent, tail[s * 2]!, `tail${s + 1}`);
    ribs.push(new Ribbon(tj.abs, tail.slice(s * 2, s * 2 + 3), 0.05 - s * 0.005, mat, rng, 0.03));
    skel[`tail${s + 1}`] = tj.p;
    parent = tj.abs;
  }
  // The coat coming apart into smoke at the ends: wisps off the back and the chest.
  const wisp = smoke(look.mane ?? look.fur, look.alpha * 0.55);
  for (let i = 0; i < 7; i++) {
    const pts: THREE.Vector3[] = [];
    const x = R() * 0.3;
    const z = R() * 0.6;
    for (let j = 0; j < 6; j++) pts.push(V(x + R() * 0.05, 0.72 + j * 0.08, z - j * 0.05));
    ribs.push(new Ribbon(z > 0.1 ? chest.abs : body.abs, pts, 0.022, wisp, rng, 0.04));
  }
  // The howler's mane: spikes standing up over the shoulders and neck, smoking at the tips.
  if (look.mane) {
    const mane = smoke(look.mane, Math.min(1, look.alpha + 0.1), { depthWrite: true });
    for (let i = 0; i < 9; i++) {
      const a = (i / 8 - 0.5) * 1.6;
      const spike = M(new THREE.ConeGeometry(0.05, 0.26, 5), mane, chest.abs);
      spike.position.set(Math.sin(a) * 0.17, 0.82 - Math.abs(a) * 0.06, 0.28 + Math.cos(a) * 0.05);
      spike.rotation.set(-0.5, 0, -a * 0.6);
    }
    // The scar across the muzzle.
    const scar = M(new THREE.BoxGeometry(0.012, 0.012, 0.16), std('#8a8f98', { roughness: 1 }), head);
    scar.position.set(0.03, 0.05, 0.16);
    scar.rotation.set(0, 0.6, 0.2);
  }
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  return done(c, 0.95, t, undefined, bonesOf(c, skel));
};

/** The howler: darker than the pack, its mane up, red eyes. */
export const howler = (o?: BaseOpts): Model => ashWolf(o, { fur: '#23242d', alpha: 0.92, eyes: '#ff4f3a', mane: '#3c3f4a', seed: 921 });

/** The Grey Wolf: pale grey, its coat smoking at the ends, red eyes. */
// (Darker than the ash it lies in, its mane's smoke pale: on the den's pale ash a mid grey Wolf disappeared.)
export const grayWolf = (o?: BaseOpts): Model => ashWolf(o, { fur: '#454b54', alpha: 0.95, eyes: '#ff4f3a', mane: '#a9b3bf', seed: 922 });

/** The old howler (chapter 3): bigger and grizzled, the one who leads when the Wolf isn't there; its mane gone pale. */
export const elderHowler = (o?: BaseOpts): Model => ashWolf(o, { fur: '#3b3e48', alpha: 0.94, eyes: '#ff4f3a', mane: '#b9bfc8', seed: 931 });

/** Bera: small, wiry, a patch over one eye, white hair cropped, her stripe of red mud, an oar on her shoulder. */
const bera = (o?: BaseOpts): Model => {
  const c = begin(o, 0.58, 923);
  const wool = cloth('#30343c');
  const skin = std('#c9a690', { roughness: 0.88 });
  const f = figure({ legs: cloth('#2a2c32'), boots: leather('#211b17'), torso: wool, shoulders: wool, arms: wool, forearms: leather('#3b3530'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.setScalar(0.88);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // A long jerkin of dark wool belted with leather, a fur collar, the horn at her belt.
  const skirt = M(new THREE.CylinderGeometry(0.2, 0.27, 0.5, 12, 1, true), std('#2b2f37', { roughness: 1, side: THREE.DoubleSide }), b);
  skirt.position.y = 0.72;
  ring(b, 0.2, 0.025, leather('#3a2c22'), 0.96, 4, 14);
  const fur = std('#6d6a66', { roughness: 1 });
  M(new THREE.TorusGeometry(0.16, 0.06, 6, 14), fur, b).position.set(0, 1.42, 0);
  const horn = M(new THREE.ConeGeometry(0.035, 0.2, 7), std('#d9d6cf', { roughness: 0.6 }), b);
  horn.position.set(0.2, 0.9, 0.06);
  horn.rotation.set(0.3, 0, 1.9);
  // The head: white hair cropped close, one thin braid; the patch on the left eye; the red stripe across.
  const white = std('#e4e2dd', { roughness: 1 });
  const hair = M(new THREE.SphereGeometry(0.14, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), white, h);
  hair.position.set(0, 1.655, -0.01);
  for (let i = 0; i < 4; i++) M(new THREE.SphereGeometry(0.022 - i * 0.003, 6, 5), white, h).position.set(0.09, 1.56 - i * 0.05, -0.09);
  const patch = M(new THREE.CylinderGeometry(0.034, 0.034, 0.012, 10), leather('#141110'), h);
  patch.position.set(0.045, 1.655, 0.117);
  patch.rotation.x = Math.PI / 2;
  ring(h, 0.138, 0.006, leather('#141110'), 1.69, 3, 16).rotation.z = 0.35;
  M(new THREE.SphereGeometry(0.013, 6, 5), std('#141214'), h).position.set(-0.042, 1.655, 0.115);
  const stripe = M(new THREE.CylinderGeometry(0.146, 0.146, 0.036, 16, 1, true, -1.25, 2.5), emis('#a8382a', '#5a1208', 0.35), h);
  stripe.position.set(0, 1.66, 0.0);
  const nose = M(new THREE.ConeGeometry(0.02, 0.06, 5), std('#b98e78', { roughness: 0.85 }), h);
  nose.position.set(0, 1.62, 0.135);
  nose.rotation.x = Math.PI / 2 + 0.2;
  M(new THREE.BoxGeometry(0.05, 0.006, 0.01), std('#6f4a44'), h).position.set(0, 1.575, 0.112);
  // The oar over her right shoulder, its blade up behind her.
  const oar = G(b, 0.12, 1.4, -0.02);
  oar.rotation.set(0.35, 0, -0.45);
  limb(oar, V(0, -0.75, 0), V(0, 0.95, 0), 0.022, 0.022, std('#5c4a3a', { roughness: 0.9 }), 6);
  const blade = M(new THREE.BoxGeometry(0.13, 0.42, 0.025), std('#6a5747', { roughness: 0.9 }), oar);
  blade.position.y = 1.05;
  M(new THREE.BoxGeometry(0.135, 0.06, 0.03), cloth(CLAN), oar).position.y = 0.84;
  f.rig.head.rotation.x = -0.05;
  return done(c, 1.42, () => undefined, f.rig);
};

/** Ivar: tall, a braided beard, a bearskin over the shoulders, a red sash, his grandfather's spear. */
const ivar = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 924);
  const leatherD = leather('#2e2925');
  const skin = std('#d0a68c', { roughness: 0.85 });
  const f = figure({ legs: cloth('#2b2d33'), boots: leather('#1f1915'), torso: leather('#3a332d'), shoulders: leatherD, arms: skin, forearms: leather('#3a332d'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(1.12, 1.12, 1.1);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The bearskin over the shoulders, its head on the left shoulder.
  const bear = std('#4a3f37', { roughness: 1, side: THREE.DoubleSide });
  M(new THREE.CylinderGeometry(0.2, 0.34, 0.5, 12, 1, true), bear, b).position.set(0, 1.24, -0.03);
  const bh = M(new THREE.SphereGeometry(0.09, 8, 6), bear, b);
  bh.position.set(-0.2, 1.48, 0.02);
  bh.scale.set(1, 0.8, 1.3);
  // The red sash across the chest.
  const sash = M(new THREE.BoxGeometry(0.08, 0.62, 0.03), cloth(CLAN), b);
  sash.position.set(0, 1.1, 0.19);
  sash.rotation.z = 0.7;
  ring(b, 0.21, 0.03, leather('#241e1a'), 0.94, 4, 14);
  // Hair tied back, the beard in two long plaits with iron rings.
  const hairC = std('#5d4a3a', { roughness: 1 });
  M(new THREE.SphereGeometry(0.145, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hairC, h).position.set(0, 1.655, -0.015);
  for (let i = 0; i < 4; i++) M(new THREE.SphereGeometry(0.03 - i * 0.003, 6, 5), hairC, h).position.set(0, 1.6 - i * 0.06, -0.15);
  const beard = M(new THREE.SphereGeometry(0.11, 10, 8, 0, Math.PI * 2, Math.PI * 0.45, Math.PI * 0.55), hairC, h);
  beard.position.set(0, 1.57, 0.06);
  beard.scale.set(1.1, 1.1, 0.9);
  const iron = metal('#4a4e55', { roughness: 0.5 });
  for (const k of [-1, 1]) {
    for (let i = 0; i < 4; i++) M(new THREE.SphereGeometry(0.026 - i * 0.003, 6, 5), hairC, h).position.set(k * 0.04, 1.45 - i * 0.05, 0.12 - i * 0.01);
    ring(h, 0.026, 0.006, iron, 1.4, 3, 8).position.set(k * 0.04, 1.4, 0.11);
    M(new THREE.SphereGeometry(0.013, 6, 5), std('#141214'), h).position.set(k * 0.044, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.05, 0.014, 0.02), hairC, h).position.set(k * 0.044, 1.682, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.022, 0.06, 5), std('#c19478', { roughness: 0.85 }), h);
  nose.position.set(0, 1.625, 0.135);
  nose.rotation.x = Math.PI / 2 + 0.25;
  // Hrolf's spear, upright in the right hand: an ash shaft, an old leaf of iron, a red wool tuft under it.
  const spear = G(f.rhAbs, 0.3, 0.9, 0.16);
  limb(spear, V(0, -0.85, 0), V(0, 1.05, 0), 0.02, 0.022, std('#6a5846', { roughness: 0.9 }), 6);
  const head = M(new THREE.ConeGeometry(0.05, 0.28, 4), metal('#6a7078', { roughness: 0.4 }), spear);
  head.position.y = 1.2;
  head.scale.z = 0.35;
  M(new THREE.SphereGeometry(0.04, 6, 5), cloth(CLAN), spear).position.y = 1.03;
  return done(c, 1.62, () => undefined, f.rig);
};

/** Sten: Ivar's younger brother, lean, no beard yet, his hair in one short tail, a wool tunic, an oar on his shoulder. */
const sten = (o?: BaseOpts): Model => {
  const c = begin(o, 0.56, 926);
  const wool = cloth('#3a414b');
  const skin = std('#d4ab92', { roughness: 0.85 });
  const f = figure({ legs: cloth('#2b2d33'), boots: leather('#231c17'), torso: wool, shoulders: leather('#3b342e'), arms: wool, forearms: leather('#3b342e'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(1.0, 1.02, 1.0);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // A short tunic over the hips, a thin red sash, a sheepskin over one shoulder.
  const skirt = M(new THREE.CylinderGeometry(0.19, 0.24, 0.32, 12, 1, true), std('#353b44', { roughness: 1, side: THREE.DoubleSide }), b);
  skirt.position.y = 0.86;
  ring(b, 0.195, 0.03, cloth(CLAN), 0.98, 4, 14);
  const fleece = M(new THREE.SphereGeometry(0.13, 8, 6), std('#8d8a86', { roughness: 1 }), b);
  fleece.position.set(-0.13, 1.42, 0);
  fleece.scale.set(1, 0.6, 1.1);
  // Brown hair, a short tail behind; a scrape of stubble; his hands bound in rag (from the rope, or the rock).
  const hairC = std('#6b5341', { roughness: 1 });
  M(new THREE.SphereGeometry(0.143, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hairC, h).position.set(0, 1.66, -0.012);
  for (let i = 0; i < 3; i++) M(new THREE.SphereGeometry(0.026 - i * 0.004, 6, 5), hairC, h).position.set(0, 1.62 - i * 0.05, -0.15);
  M(new THREE.SphereGeometry(0.1, 10, 6, 0, Math.PI * 2, Math.PI * 0.55, Math.PI * 0.3), std('#8a6c58', { roughness: 1 }), h).position.set(0, 1.6, 0.03);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.013, 6, 5), std('#141214'), h).position.set(k * 0.044, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.045, 0.012, 0.02), hairC, h).position.set(k * 0.044, 1.68, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.02, 0.055, 5), std('#c39a80', { roughness: 0.85 }), h);
  nose.position.set(0, 1.625, 0.135);
  nose.rotation.x = Math.PI / 2 + 0.25;
  M(new THREE.SphereGeometry(0.05, 6, 5), std('#c9c3ba', { roughness: 1 }), f.rhAbs).position.set(0, 0, 0);
  // The oar over his left shoulder, its blade down behind him.
  const oar = G(b, -0.12, 1.36, -0.04);
  oar.rotation.set(-0.3, 0, 0.5);
  limb(oar, V(0, -0.9, 0), V(0, 0.8, 0), 0.022, 0.022, std('#5c4a3a', { roughness: 0.9 }), 6);
  const blade = M(new THREE.BoxGeometry(0.13, 0.4, 0.025), std('#6a5747', { roughness: 0.9 }), oar);
  blade.position.y = -1.05;
  return done(c, 1.6, () => undefined, f.rig);
};

/**
 * Sigrun (chapter 4, the memory's face): the huntress seventy years back, the thirty-first night. Tall and lean, her
 * black hair braided with grey, her face burnt by the cold (red at the cheeks and nose), a wolf's skin over her
 * shoulders with its head on her left shoulder, a wool coat to the knees, her axe in the right hand, the hunting paint
 * of the clan dark red down her forearms.
 */
const sigrun = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 927);
  const wool = cloth('#3a3f47');
  const skin = std('#cfa592', { roughness: 0.86 });
  const paint = emis('#8e2a20', '#3a0c06', 0.3);
  const f = figure({ legs: cloth('#2a2d33'), boots: leather('#1f1915'), torso: wool, shoulders: wool, arms: wool, forearms: paint, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.96, 1.1, 0.96);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The coat to the knees, belted; the wolf's skin over the shoulders, grey with its pale belly, its head on the left.
  M(new THREE.CylinderGeometry(0.19, 0.26, 0.56, 12, 1, true), std('#353a42', { roughness: 1, side: THREE.DoubleSide }), b).position.y = 0.7;
  ring(b, 0.195, 0.026, leather('#2b2420'), 0.97, 4, 14);
  const pelt = std('#7c8086', { roughness: 1, side: THREE.DoubleSide });
  M(new THREE.CylinderGeometry(0.19, 0.33, 0.52, 12, 1, true), pelt, b).position.set(0, 1.22, -0.03);
  M(new THREE.TorusGeometry(0.17, 0.055, 6, 14), std('#a3a7ac', { roughness: 1 }), b).position.set(0, 1.43, 0);
  const wh = M(new THREE.SphereGeometry(0.085, 8, 6), pelt, b);
  wh.position.set(0.19, 1.5, 0.04);
  wh.scale.set(1, 0.75, 1.35);
  const sn = M(new THREE.ConeGeometry(0.04, 0.12, 6), pelt, b);
  sn.position.set(0.2, 1.48, 0.16);
  sn.rotation.x = Math.PI / 2;
  for (const k of [-1, 1]) M(new THREE.ConeGeometry(0.025, 0.07, 4), pelt, b).position.set(0.19 + k * 0.04, 1.56, 0.0);
  // Black hair with grey through it, one long braid down the back; the cold's red on cheeks and nose.
  const hairC = std('#1d1c20', { roughness: 1 });
  const grey = std('#8d9096', { roughness: 1 });
  M(new THREE.SphereGeometry(0.143, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hairC, h).position.set(0, 1.66, -0.012);
  for (let i = 0; i < 7; i++) M(new THREE.SphereGeometry(0.032 - i * 0.003, 6, 5), i % 2 ? grey : hairC, h).position.set(0, 1.6 - i * 0.065, -0.15 - i * 0.008);
  M(new THREE.BoxGeometry(0.03, 0.012, 0.01), grey, h).position.set(0.06, 1.75, 0.08);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.013, 6, 5), std('#141214'), h).position.set(k * 0.044, 1.656, 0.114);
    M(new THREE.SphereGeometry(0.03, 6, 5), std('#c97f74', { roughness: 0.9 }), h).position.set(k * 0.07, 1.615, 0.095);
  }
  const nose = M(new THREE.ConeGeometry(0.02, 0.06, 5), std('#c98577', { roughness: 0.85 }), h);
  nose.position.set(0, 1.62, 0.135);
  nose.rotation.x = Math.PI / 2 + 0.2;
  M(new THREE.BoxGeometry(0.05, 0.006, 0.01), std('#6f4a44'), h).position.set(0, 1.575, 0.112);
  // Her axe in the right hand: a long haft, a bearded head of dark iron, a red wool tuft.
  const axe = G(f.rhAbs, 0.3, 0.9, 0.16);
  limb(axe, V(0, -0.55, 0), V(0, 0.55, 0), 0.02, 0.022, std('#5c4a3a', { roughness: 0.9 }), 6);
  const bit = M(new THREE.BoxGeometry(0.03, 0.2, 0.17), metal('#5b6068', { roughness: 0.45 }), axe);
  bit.position.set(0, 0.5, 0.08);
  M(new THREE.SphereGeometry(0.035, 6, 5), cloth(CLAN), axe).position.y = 0.36;
  return done(c, 1.72, () => undefined, f.rig);
};

/**
 * The Ash Seal (chapter 4, the memory's boss): a seal of ash, huge and grey, lying low on the ice; its hide coming apart
 * into smoke along the back, a pale belly, whiskers of ash, red eyes. Rigged as the pack (body, chest, head, jaw, four
 * limbs as flippers), so the wolves' clips move it; flat on the belly, so it slides as it is.
 */
export const ashSeal = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 941);
  const { g, R, rng } = c;
  const hide = smoke('#5a5f68', 0.97, { depthWrite: true });
  const belly = std('#9aa0a8', { roughness: 0.9 });
  const ribs: Ribbon[] = [];
  const body = joint(g, V(0, 0.32, -0.1), 'body');
  const chest = joint(body.abs, V(0, 0.36, 0.3), 'chest');
  const neck = joint(chest.abs, V(0, 0.42, 0.55), 'head');
  const jaw = joint(neck.abs, V(0, 0.42, 0.78), 'jaw');
  const bm = M(new THREE.SphereGeometry(0.3, 14, 10), hide, body.abs);
  bm.scale.set(1.05, 0.72, 2.0);
  bm.position.set(0, 0.32, -0.15);
  const bb = M(new THREE.SphereGeometry(0.28, 12, 8), belly, body.abs);
  bb.scale.set(0.95, 0.4, 1.8);
  bb.position.set(0, 0.18, -0.1);
  M(new THREE.SphereGeometry(0.27, 12, 9), hide, chest.abs).position.set(0, 0.38, 0.32);
  const head = G(neck.abs, 0, 0.47, 0.66);
  const hm = M(new THREE.SphereGeometry(0.17, 12, 9), hide, head);
  hm.scale.set(1, 0.9, 1.15);
  const snout = M(new THREE.SphereGeometry(0.1, 10, 8), std('#4c5058', { roughness: 0.9 }), head);
  snout.position.set(0, -0.04, 0.16);
  snout.scale.set(1.1, 0.8, 1);
  const jawG = G(jaw.abs, 0, 0.47, 0.66);
  M(new THREE.SphereGeometry(0.07, 8, 6), belly, jawG).position.set(0, -0.1, 0.13);
  eyesGlow(head, '#ff4f3a', 0.03, 0.05, 0.13);
  for (const k of [-1, 1])
    for (let i = 0; i < 3; i++) {
      const w = M(new THREE.CylinderGeometry(0.004, 0.002, 0.16, 3), std('#c8ccd1'), head);
      w.position.set(k * 0.1, -0.04 - i * 0.015, 0.2);
      w.rotation.set(0, 0, k * (1.2 + i * 0.15));
    }
  const skel: Record<string, THREE.Object3D> = { body: body.p, chest: chest.p, head: neck.p, jaw: jaw.p };
  // The flippers: two at the chest, flat and out; two at the tail, together, as a seal's.
  for (const [x, z, front] of [[0.3, 0.32, true], [-0.3, 0.32, true], [0.1, -0.62, false], [-0.1, -0.62, false]] as const) {
    const side = x < 0 ? 'L' : 'R';
    const name = front ? 'fore' : 'hind';
    const j = joint(front ? chest.abs : body.abs, V(x, 0.2, z), `${name}${side}`);
    const fl = M(new THREE.SphereGeometry(0.16, 8, 6), hide, j.abs);
    fl.scale.set(front ? 1.4 : 0.6, 0.18, front ? 0.6 : 1.5);
    fl.position.set(x + (front ? Math.sign(x) * 0.12 : 0), 0.12, z + (front ? 0.02 : -0.16));
    skel[`${name}${side}`] = j.p;
  }
  // The hide smoking away along the back.
  const wisp = smoke('#7d828a', 0.5);
  for (let i = 0; i < 9; i++) {
    const pts: THREE.Vector3[] = [];
    const x = R() * 0.25;
    const z = -0.6 + i * 0.13;
    for (let k = 0; k < 6; k++) pts.push(V(x + R() * 0.05, 0.52 + k * 0.08, z - k * 0.05));
    ribs.push(new Ribbon(z > 0.15 ? chest.abs : body.abs, pts, 0.03, wisp, rng, 0.04));
  }
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  return done(c, 0.95, t, undefined, bonesOf(c, skel));
};

/** Terrón's place on the board (engine/caza.ts draws him walking with the herd's legs): nothing but a speck. */
const terronStub = (o?: BaseOpts): Model => {
  const c = begin(o, 0.3, 925);
  M(new THREE.SphereGeometry(0.01, 4, 3), std('#30343c'), c.g).position.y = 0.6;
  return done(c, 0.6, () => undefined);
};

/** The Long Hunt's people by name (models/sendas.ts asks here), or null. */
export const cazaModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'bera':
      return bera(o);
    case 'ivar':
      return ivar(o);
    case 'sten':
      return sten(o);
    case 'terron':
      return terronStub(o);
    case 'sigrun':
      return sigrun(o);
    default:
      return null;
  }
};
